// The active month on the Overview (shared/months.ts): the month title opens the list of months,
// arrows step through them, a quiet banner on a month that is not the active one ("Viewing
// October", "Back to November"), and the new month card while the calendar is past the active
// month ("It's November. Start your November budget."). Changing month goes through go() in main.ts.
import type { OverviewDto } from '../../../shared/api';
import { BACK_TO, PLAN_EVERY_MONTH, PLAN_ONLY_HERE, monthWithYear, NEW_MONTH_BODY, NEW_MONTH_BUTTON, NEW_MONTH_NUDGE, NEW_MONTH_TITLE, NEW_MONTH_WAITING, VIEWING } from '../../../shared/months';
import { esc } from '../format';

/** The month before and after the one shown, among the months that can be opened (null at either end). */
export function monthSteps(d: OverviewDto): { prev: string | null; next: string | null } {
  const i = d.months.indexOf(d.month.key);
  return { prev: i >= 0 && i + 1 < d.months.length ? d.months[i + 1] : null, next: i > 0 ? d.months[i - 1] : null };
}

/** Previous and next month, beside the title (disabled at the ends). */
export function monthArrows(d: OverviewDto): string {
  const { prev, next } = monthSteps(d);
  const arrow = (to: string | null, label: string, glyph: string) =>
    `<button class="montharrow" data-a="month-go" data-month="${esc(to ?? '')}" aria-label="${esc(label)}"${to ? '' : ' disabled'}>${glyph}</button>`;
  return `${arrow(prev, prev ? `Previous month, ${monthWithYear(prev)}` : 'No earlier month', '&#8249;')}${arrow(next, next ? `Next month, ${monthWithYear(next)}` : 'No later month', '&#8250;')}`;
}

/** Every month, newest first; the one shown is pressed, the active one says so. */
export function vMonthSheet(d: OverviewDto): string {
  const rows = d.months
    .map(
      (m) =>
        `<button class="monthrow" data-a="month-go" data-month="${esc(m)}" aria-pressed="${m === d.month.key}"><span>${esc(monthWithYear(m))}</span>${
          m === d.month.activeKey ? '<small class="muted">Current budget</small>' : ''
        }</button>`,
    )
    .join('');
  return `<div class="grab"></div><h2>Months</h2><div class="monthlist">${rows}</div>
  <div class="btns"><button class="btn" data-a="close">Close</button></div>`;
}

/**
 * On a month that is not the active one: a quiet line with one tap back, and where an edited
 * planned amount goes (that month only when it kept its own plan, else every month). On Overview,
 * the Budget tab and a category screen.
 */
export function vMonthBanner(d: OverviewDto): string {
  if (d.month.isActive) return '';
  const plan = d.month.planSnapshot ? PLAN_ONLY_HERE(d.month.key) : PLAN_EVERY_MONTH;
  return `<div class="monthbanner" role="status"><span>${esc(VIEWING(d.month.key))}<small class="monthplan">${esc(plan)}</small></span><button class="linkbtn" data-a="month-go" data-month="">${esc(BACK_TO(d.month.activeKey))}</button></div>`;
}

/** "It's November. Start your November budget.": only on the active month, only while the calendar is past it. */
export function vNewMonthCard(d: OverviewDto): string {
  const n = d.newMonth;
  if (!n || !d.month.isActive) return '';
  return `<section class="ask newmonth"><h2>${esc(NEW_MONTH_TITLE(n.calendar))}</h2><p>${esc(NEW_MONTH_BODY(n.next))}</p>${
    n.waiting ? `<p class="hint">${esc(NEW_MONTH_WAITING(n.waiting, n.next))}</p>` : ''
  }${n.nudge ? `<p class="hint monthnudge">${esc(NEW_MONTH_NUDGE(d.month.activeKey, n.next))}</p>` : ''}
  <div class="acts"><button class="btn primary" data-a="month-start" data-month="${esc(n.next)}">${esc(NEW_MONTH_BUTTON(n.next))}</button></div></section>`;
}
